// src/pages/admin/AdminManualFunding.jsx
import { useEffect, useState, useCallback } from "react";
import adminApi from "../../api/adminApi";
import { Banknote, RefreshCw, CheckCircle2, XCircle, Image as ImageIcon, X, Clock } from "lucide-react";
import { toast } from "react-toastify";

const naira = (n) => "₦" + Math.round(Number(n) || 0).toLocaleString("en-NG");

const TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function AdminManualFunding() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [amounts, setAmounts] = useState({});
  const [viewer, setViewer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/manual-funding", { params: { status: tab } });
      if (res.data.success) {
        setItems(res.data.requests || []);
        setPendingCount(res.data.pendingCount || 0);
      }
    } catch (err) {
      toast.error("Failed to load manual funding requests");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (viewer && viewer.url) URL.revokeObjectURL(viewer.url);
    };
  }, [viewer]);

  const approve = async (r) => {
    const typed = amounts[r._id];
    const finalAmount = typed !== undefined && typed !== "" ? Number(typed) : r.amount;
    if (!(finalAmount > 0)) {
      toast.error("Enter a valid amount");
      return;
    }
    const ok = window.confirm(
      "Credit " + naira(finalAmount) + " to " + r.fullName + "? The funding bonus is added automatically if the amount qualifies."
    );
    if (!ok) return;

    setBusyId(r._id);
    try {
      const body = typed !== undefined && typed !== "" ? { amount: Number(typed) } : {};
      const res = await adminApi.post("/manual-funding/" + r._id + "/approve", body);
      toast.success(res.data.message || "Approved");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Approval failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (r) => {
    const note = window.prompt("Reason for rejecting (optional, the user will see it):", "");
    if (note === null) return;
    setBusyId(r._id);
    try {
      const res = await adminApi.post("/manual-funding/" + r._id + "/reject", { note });
      toast.success(res.data.message || "Rejected");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Rejection failed");
    } finally {
      setBusyId(null);
    }
  };

  const openReceipt = async (r) => {
    setViewer({ loading: true, url: "" });
    try {
      const res = await adminApi.get("/manual-funding/" + r._id + "/receipt", { responseType: "blob" });
      setViewer({ loading: false, url: URL.createObjectURL(res.data) });
    } catch (err) {
      setViewer(null);
      toast.error("Could not load the receipt");
    }
  };

  const closeViewer = () => {
    setViewer(null);
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
            <Banknote size={17} className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Manual Funding Requests</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Check the transfer landed in your account, then approve to credit the wallet.
            </p>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-2.5 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-purple-400 transition"
        >
          <RefreshCw size={15} className={"text-gray-500 " + (loading ? "animate-spin" : "")} />
        </button>
      </div>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              "px-4 py-2 text-sm font-medium rounded-lg border transition " +
              (tab === t.id
                ? "bg-purple-600 text-white border-purple-600"
                : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-purple-400")
            }
          >
            {t.label}
            {t.id === "pending" && pendingCount > 0 ? " (" + pendingCount + ")" : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw size={22} className="text-purple-500 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl py-14 text-center">
          <Clock size={30} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No {tab} requests</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div
              key={r._id}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{r.fullName}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{r.email}</p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    {new Date(r.createdAt).toLocaleString("en-NG")}
                    {r.reference ? " · Ref: " + r.reference : ""}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{naira(r.amount)}</p>
                  {r.status === "approved" && (
                    <p className="text-[11px] text-green-600 dark:text-green-400">
                      Credited {naira(r.creditedAmount || r.amount)}
                      {r.bonusAmount > 0 ? " + " + naira(r.bonusAmount) + " bonus (" + r.bonusPercent + "%)" : ""}
                    </p>
                  )}
                  {r.status === "rejected" && (
                    <p className="text-[11px] text-red-500">Rejected{r.adminNote ? ": " + r.adminNote : ""}</p>
                  )}
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                {r.hasReceipt ? (
                  <button
                    onClick={() => openReceipt(r)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition"
                  >
                    <ImageIcon size={13} /> View receipt
                  </button>
                ) : (
                  <span className="text-[11px] text-gray-400">No receipt stored (check your email)</span>
                )}

                {r.status === "pending" && (
                  <>
                    <input
                      type="number"
                      min="1"
                      value={amounts[r._id] !== undefined ? amounts[r._id] : ""}
                      onChange={(e) => setAmounts((p) => ({ ...p, [r._id]: e.target.value }))}
                      placeholder={"Amount: " + r.amount}
                      className="w-36 px-3 py-2 text-xs bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 dark:text-white font-mono"
                    />
                    <button
                      onClick={() => approve(r)}
                      disabled={busyId === r._id}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition"
                    >
                      <CheckCircle2 size={13} /> {busyId === r._id ? "Working..." : "Approve"}
                    </button>
                    <button
                      onClick={() => reject(r)}
                      disabled={busyId === r._id}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-red-600 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-60 rounded-lg transition"
                    >
                      <XCircle size={13} /> Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {viewer && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
          onClick={closeViewer}
        >
          <div
            className="relative max-w-lg w-full max-h-[90vh] overflow-auto rounded-2xl bg-white dark:bg-gray-900 p-3"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeViewer}
              aria-label="Close"
              className="absolute top-2 right-2 p-1.5 rounded-full bg-black/50 text-white"
            >
              <X size={16} />
            </button>
            {viewer.loading ? (
              <div className="flex justify-center py-16">
                <RefreshCw size={22} className="text-purple-500 animate-spin" />
              </div>
            ) : (
              <img src={viewer.url} alt="Receipt" className="w-full rounded-lg" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}